import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Plus, LogOut, ChevronDown, Heart, LayoutDashboard } from 'lucide-react';
import { Button } from './ui/button';
import { navLinks } from '../mock';
import { useAuth } from '../context/AuthContext';
import NotificationsBell from './NotificationsBell';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from './ui/dropdown-menu';

const BadgeChip = ({ label }) => {
  const color = label === 'LIVE' ? 'bg-rose-500 text-white' : label === 'B2B' ? 'bg-sky-600 text-white' : label === 'PRO' ? 'bg-amber-500 text-black' : 'bg-emerald-600 text-white';
  return (
    <span className={`ml-1 text-[9px] tracking-wider font-bold px-1.5 py-0.5 rounded ${color}`}>{label}</span>
  );
};

export default function Header({ onCreate }) {
  const [open, setOpen] = useState(false);
  const { user, logout, setAuthOpen, requireAuth } = useAuth();
  const navigate = useNavigate();

  const handleCreate = () => {
    if (requireAuth('create-joyeuse')) {
      onCreate && onCreate('Joyeuse');
    }
  };

  const scrollToCagnottes = () => {
    const el = document.getElementById('cagnottes');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header className="sticky top-0 z-50 bg-[#f1f5e8]/85 backdrop-blur-md border-b border-black/5">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2 shrink-0">
          <div className="w-9 h-9 rounded-lg bg-[#1a1d1a] text-white flex items-center justify-center font-bold tracking-tight">F</div>
          <div className="leading-none">
            <div className="text-[17px] font-semibold text-[#1a1d1a]">FodindinCagnotte</div>
            <div className="text-[9px] tracking-[0.18em] uppercase text-neutral-500 mt-0.5">Solidarité</div>
          </div>
        </a>

        <nav className="hidden xl:flex items-center gap-1 text-[13.5px] text-neutral-700">
          {navLinks.map((l) => (
            <a key={l.label} href={l.href} className="px-2.5 py-2 rounded-md hover:text-[#1a1d1a] hover:bg-black/5 transition-colors flex items-center">
              <span>{l.label}</span>
              {l.badge && <BadgeChip label={l.badge} />}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button onClick={scrollToCagnottes} className="btn-anim rounded-full bg-[#5a8268] hover:bg-[#4a6f57] text-white px-4 h-10 text-[13.5px] font-medium hidden sm:inline-flex">
            <Heart className="w-4 h-4 mr-1" /> Faire un don
          </Button>
          {user && <NotificationsBell />}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="hidden md:flex items-center gap-2 px-2.5 py-1.5 rounded-full hover:bg-black/5 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-[#5a8268] text-white flex items-center justify-center text-[13px] font-semibold">
                    {(user.name || user.phone || '?').substring(0, 1).toUpperCase()}
                  </div>
                  <span className="text-[13.5px] text-neutral-800 max-w-[120px] truncate">{user.name || user.phone}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-neutral-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-[13px] font-medium">{user.name || 'Utilisateur'}</div>
                  <div className="text-[11.5px] text-neutral-500">{user.phone}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Mon tableau de bord
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onCreate && onCreate('Joyeuse')}>Créer une Cagnotte Joyeuse</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onCreate && onCreate('Yako')}>Créer une Cagnotte Yako</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-rose-600 focus:text-rose-700"><LogOut className="w-4 h-4 mr-2" /> Se déconnecter</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button onClick={() => setAuthOpen(true)} className="hidden md:inline text-[13.5px] text-neutral-700 hover:text-black px-3 py-2">Se connecter</button>
          )}
          <Button onClick={handleCreate} className="btn-anim rounded-full bg-[#1a1d1a] hover:bg-black text-white px-4 h-10 text-[13.5px] font-medium">
            <Plus className="w-4 h-4 mr-1" /> Créer une cagnotte
          </Button>
          <button className="xl:hidden p-2 rounded-md hover:bg-black/5" onClick={() => setOpen(!open)} aria-label="menu">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="xl:hidden border-t border-black/5 bg-[#f1f5e8]">
          <div className="px-4 py-3 grid gap-1">
            {navLinks.map(l => (
              <a key={l.label} href={l.href} onClick={() => setOpen(false)} className="py-2 text-[14px] text-neutral-800 flex items-center">
                {l.label}{l.badge && <BadgeChip label={l.badge} />}
              </a>
            ))}
            <button onClick={() => { setOpen(false); scrollToCagnottes(); }} className="py-2 text-left text-[14px] text-[#5a8268] font-medium">❤ Faire un don</button>
            {user ? (
              <>
                <div className="pt-2 mt-2 border-t border-black/5 text-[12px] text-neutral-500">Connecté : {user.name || user.phone}</div>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="py-2 text-left text-[14px] text-neutral-800 flex items-center gap-2"><LayoutDashboard className="w-4 h-4" /> Mon tableau de bord</Link>
                <button onClick={() => { logout(); setOpen(false); }} className="py-2 text-left text-[14px] text-rose-600">Se déconnecter</button>
              </>
            ) : (
              <button onClick={() => { setOpen(false); setAuthOpen(true); }} className="py-2 text-left text-[14px] text-neutral-800">Se connecter</button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
